import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Handshake, Building2, FileText, Tag, ArrowUpDown, ChevronDown, X as XIcon,
  Search, Plus, Check, Globe, Loader2,
} from 'lucide-react';
import { loggedGet } from './loggedFetch';
import logger from '../../lib/logger';

/*
 * PartnerEcosystem — ranks the resellers / channel partners actually
 * pushing the target vendor's technology across CA state procurement.
 *
 * Two modes:
 *   - State-wide (default): top N resellers ranked by total CA spend
 *   - Agency-scoped: when an agency is selected, the same ranking
 *     scoped to that single agency. The user can then see "which
 *     channel partner do I need to meet with for expansion at THIS
 *     account" — distinct from the state-wide question.
 *
 * The agency picker reads the new /:vendor/agencies endpoint (cheap,
 * one row per agency where the vendor has any spend). The partner
 * rollup query passes ?department_id when an agency is selected.
 */

function formatCurrency(num) {
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function PartnerEcosystem({ vendor, includeLowConfidence, strict, productFamily, departmentIds, listName, years, onSeedPlan }) {
  const [scopedAgency, setScopedAgency] = useState(null); // { department_id, acronym, official_name }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [addedRows, setAddedRows] = useState({});
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pickerOpen]);

  // Reset scoped agency when the vendor changes — different vendor =
  // different list of agencies, so old selection wouldn't make sense.
  useEffect(() => {
    setScopedAgency(null);
    setAddedRows({});
  }, [vendor]);

  const baseQs = new URLSearchParams();
  if (includeLowConfidence) baseQs.set('includeLowConfidence', 'true');
  if (!strict) baseQs.set('strict', 'false');
  // /agencies honors product_family; /partners ignores it today (matview
  // limitation), but we send it for forward-compat so the chip starts
  // narrowing partners as soon as mv_partner_rollup is rebuilt with
  // component_id grain.
  if (productFamily) baseQs.set('product_family', productFamily);
  // Global time filter from TimeFilterContext (null = all-time)
  if (years != null) baseQs.set('years', String(years));

  // Agency picker source — list of agencies where the vendor has spend.
  const agenciesQuery = useQuery({
    queryKey: ['vendor-analysis', 'agencies', vendor, includeLowConfidence, strict, productFamily, years],
    queryFn: loggedGet(
      'PartnerEcosystem:agencies',
      `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/agencies${baseQs.toString() ? `?${baseQs}` : ''}`
    ),
    enabled: !!vendor,
  });

  // Partner rollup — scoped to selected agency when present.
  // Phase H.29: departmentIds from list scoping takes precedence unless user
  // picks a specific agency from the internal picker (scopedAgency).
  const partnerQs = new URLSearchParams(baseQs);
  partnerQs.set('limit', '25');
  if (scopedAgency) {
    // User's agency picker selection takes precedence
    partnerQs.set('department_id', String(scopedAgency.department_id));
  } else if (departmentIds?.length) {
    // List scoping — use array format
    partnerQs.set('department_ids', departmentIds.join(','));
  }
  const path = `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/partners?${partnerQs}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-analysis', 'partners', vendor, includeLowConfidence, strict, scopedAgency?.department_id || null, departmentIds, productFamily, years],
    queryFn: loggedGet('PartnerEcosystem:fetch', path),
    enabled: !!vendor,
  });

  const filteredAgencies = useMemo(() => {
    const list = agenciesQuery.data?.agencies || [];
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(a =>
      (a.acronym || '').toLowerCase().includes(q) ||
      (a.official_name || '').toLowerCase().includes(q)
    );
  }, [agenciesQuery.data, pickerQuery]);

  const totals = useMemo(() => {
    if (!data?.partners) return null;
    return {
      partner_count: data.partner_count,
      total_spend: data.summary?.total_spend || 0,
    };
  }, [data]);

  const handleSeedPartner = (partner) => {
    if (!onSeedPlan) return;
    const scopeLabel = scopedAgency ? ` at ${scopedAgency.acronym}` : ' across CA state';
    onSeedPlan({
      kind: 'partner-engagement',
      title: `Engage ${partner.supplier_name}${scopeLabel} for ${vendor} expansion`,
      rationale: scopedAgency
        ? `${partner.supplier_name} pushes ${formatCurrency(partner.total_spend)} of ${vendor} tech at ${scopedAgency.acronym} (${scopedAgency.official_name || ''}) across ${partner.po_count} POs and ${partner.po_line_count} line items. Open a partner-led expansion conversation to find the next adjacent opportunity inside this account.`
        : `${partner.supplier_name} pushes ${formatCurrency(partner.total_spend)} of ${vendor} tech across ${partner.agency_count} agencies and ${partner.po_count} POs state-wide. They have the most leverage to introduce ${vendor} to expansion conversations and the most to lose if you go around them.`,
      dollar_impact: scopedAgency ? partner.total_spend : null,
      partner_name: partner.supplier_name,
      department_id: scopedAgency?.department_id || null,
      agency: scopedAgency?.acronym || null,
      partner_summary: {
        total_spend: partner.total_spend,
        agency_count: partner.agency_count,
        po_count: partner.po_count,
        po_line_count: partner.po_line_count,
        top_categories: partner.top_categories,
      },
    });
    setAddedRows(prev => ({ ...prev, [partner.supplier_name]: true }));
    logger.vendorAnalysis.info('PartnerEcosystem:seed-plan', {
      vendor, partner: partner.supplier_name, scoped_agency: scopedAgency?.acronym || null,
    });
  };

  const headerSub = scopedAgency
    ? <>
        Scoped to <strong>{scopedAgency.acronym}</strong>
        {scopedAgency.official_name && <> · {scopedAgency.official_name}</>}
        {' · '}who's pushing {vendor}'s tech inside this agency.
      </>
    : <>
        Resellers and channel partners ranked by spend across <strong>all CA state agencies</strong>.
        These are the conversations to have for expansion — partners with
        the biggest book have the most leverage to make introductions and
        the most to lose if you bypass them.
      </>;

  return (
    <div className="vca-section">
      <div className="vca-section-head">
        <div>
          <h2>
            <Handshake size={16} style={{ verticalAlign: 'text-bottom' }} /> Partner ecosystem · who's pushing {vendor}'s tech
          </h2>
          <p className="vca-section__sub">
            {headerSub}
            {totals && (
              <>
                {' '}<strong>{totals.partner_count}</strong> partners moving
                {' '}<strong>{formatCurrency(totals.total_spend)}</strong>
                {scopedAgency ? ' inside this agency' : ' state-wide'}.
              </>
            )}
          </p>
        </div>
        <div className="vca-partners-scope" ref={pickerRef}>
          <button
            type="button"
            className={`vca-renewal-agency-trigger ${scopedAgency ? 'is-active' : ''}`}
            onClick={() => setPickerOpen(o => !o)}
            disabled={agenciesQuery.isLoading}
          >
            {scopedAgency ? <Building2 size={11} /> : <Globe size={11} />}
            <span>
              {scopedAgency ? scopedAgency.acronym : 'All agencies'}
            </span>
            <ChevronDown size={11} />
          </button>
          {scopedAgency && (
            <button
              type="button"
              className="vca-partners-scope-clear"
              onClick={() => setScopedAgency(null)}
              title="Back to state-wide view"
            >
              <XIcon size={11} /> Clear
            </button>
          )}
          {pickerOpen && (
            <div className="vca-renewal-agency-menu" role="listbox">
              <div className="vca-renewal-agency-search">
                <Search size={12} />
                <input
                  autoFocus
                  type="text"
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder="Search agencies…"
                />
              </div>
              <div className="vca-renewal-agency-list">
                <button
                  type="button"
                  className="vca-renewal-agency-row"
                  onClick={() => {
                    setScopedAgency(null);
                    setPickerOpen(false);
                    setPickerQuery('');
                  }}
                  style={{ textAlign: 'left', background: scopedAgency ? 'transparent' : 'var(--color-surface-hover)' }}
                >
                  <Globe size={12} />
                  <span className="vca-renewal-agency-name">All agencies (state-wide)</span>
                </button>
                {agenciesQuery.isLoading && (
                  <div className="vca-renewal-agency-empty">
                    <Loader2 size={12} className="vca-spin" /> Loading agencies…
                  </div>
                )}
                {!agenciesQuery.isLoading && filteredAgencies.length === 0 && (
                  <div className="vca-renewal-agency-empty">
                    {pickerQuery ? 'No agencies match.' : 'No agencies have recorded spend yet.'}
                  </div>
                )}
                {filteredAgencies.map(a => {
                  const isActive = scopedAgency?.department_id === a.department_id;
                  return (
                    <button
                      type="button"
                      key={a.department_id}
                      className={`vca-renewal-agency-row ${isActive ? 'is-checked' : ''}`}
                      onClick={() => {
                        setScopedAgency(a);
                        setPickerOpen(false);
                        setPickerQuery('');
                      }}
                      style={{ textAlign: 'left' }}
                    >
                      <span className="vca-renewal-agency-name">
                        {a.acronym}
                        {a.official_name && a.official_name !== a.acronym && (
                          <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}> · {a.official_name}</span>
                        )}
                      </span>
                      <span className="vca-renewal-agency-meta">
                        {formatCurrency(a.vendor_spend)} · {a.po_count} POs
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="vca-section__loading">
          <Loader2 size={14} className="vca-spin" style={{ verticalAlign: 'middle' }} /> Loading partner ecosystem…
        </div>
      )}
      {error && (
        <div className="vca-section__error">Partners failed: {error.message}</div>
      )}

      {!isLoading && !error && data && data.partners.length === 0 && (
        <p className="vca-section__sub">
          {scopedAgency
            ? `No reseller-level data available for ${vendor} at ${scopedAgency.acronym} yet.`
            : `No reseller-level data available for ${vendor} yet — either no classified spend or supplier names are blank.`}
        </p>
      )}

      {!isLoading && !error && data && data.partners.length > 0 && (
        <div className="vca-partner-table-wrap">
          <table className="vca-table vca-partner-table">
            <thead>
              <tr>
                <th className="vca-num">#</th>
                <th>Partner</th>
                <th className="vca-num"><ArrowUpDown size={11} style={{ verticalAlign: 'middle', opacity: 0.5 }} /> Spend</th>
                <th className="vca-num"><Building2 size={11} style={{ verticalAlign: 'middle', opacity: 0.5 }} /> Agencies</th>
                <th className="vca-num"><FileText size={11} style={{ verticalAlign: 'middle', opacity: 0.5 }} /> POs · lines</th>
                <th><Tag size={11} style={{ verticalAlign: 'middle', opacity: 0.5 }} /> Top categories</th>
                <th>{scopedAgency ? '' : 'Sample agencies'}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.partners.map((p, i) => {
                const added = !!addedRows[p.supplier_name];
                return (
                  <tr key={p.supplier_name}>
                    <td className="vca-num vca-partner-rank">{i + 1}</td>
                    <td className="vca-partner-name">
                      {p.supplier_name}
                      {p.is_canonical && (
                        <span
                          className="vca-canonical-badge"
                          title="Canonical name: This supplier's spend has been consolidated from multiple variations (e.g., CDW-G, CDW Government)"
                        >
                          Canonical
                        </span>
                      )}
                    </td>
                    <td className="vca-num vca-partner-spend">{formatCurrency(p.total_spend)}</td>
                    <td className="vca-num">
                      {scopedAgency ? '—' : p.agency_count}
                    </td>
                    <td className="vca-num vca-partner-pos">
                      {p.po_count.toLocaleString()}
                      <span className="vca-partner-pos-sub"> · {p.po_line_count.toLocaleString()}</span>
                    </td>
                    <td>
                      <div className="vca-partner-chips">
                        {(p.top_categories || []).map((c, idx) => (
                          <span key={idx} className="vca-partner-chip">{c}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      {!scopedAgency && (
                        <div className="vca-partner-chips vca-partner-chips--muted">
                          {(p.sample_agencies || []).map((a, idx) => (
                            <span key={idx} className="vca-partner-chip vca-partner-chip--muted">{a}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {onSeedPlan && (
                        <button
                          type="button"
                          className="vca-add-btn"
                          onClick={() => handleSeedPartner(p)}
                          disabled={added}
                          title={
                            scopedAgency
                              ? `Seed a play to engage ${p.supplier_name} for expansion at ${scopedAgency.acronym}`
                              : `Seed a play to open a partner conversation with ${p.supplier_name} state-wide`
                          }
                        >
                          {added ? <Check size={12} /> : <Plus size={12} />}
                          <span>{added ? 'Added' : 'Add to plan'}</span>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PartnerEcosystem;
